import { REALTIME_SUBSCRIBE_STATES, type RealtimeChannel } from '@supabase/supabase-js';
import { environnement } from '@/config/env';
import { supabase } from './client';

/**
 * Le direct : quand une ligne change dans la base, les écrans ouverts se
 * relisent, sans attendre qu'on recharge.
 *
 * Un seul canal pour toute l'application, ouvert dès qu'une session existe et
 * refermé à la déconnexion. Il ne transporte pas les données : il dit « quelque
 * chose a changé », et chaque écran relit ce qu'il affiche, avec ses propres
 * règles d'accès. Les règles par ligne s'appliquent aussi aux événements : un
 * partenaire n'est prévenu que de ce qu'il peut lire.
 *
 * Une validation touche plusieurs tables d'un coup (l'étape, le chantier, une
 * relance) : les événements d'une même rafale sont regroupés, l'écran ne relit
 * qu'une fois.
 */
export const TABLES_EN_DIRECT = [
  'chantiers',
  'etapes_chantier',
  'preuves',
  'tickets_sav',
  'preuves_sav',
  'modele_etapes',
  'types_document',
  'relances',
  'courriels',
  'releves',
  'chantiers_finances',
  'factures',
  'verifications_toiture',
] as const;

/** Le silence attendu après le dernier événement d'une rafale avant de prévenir. */
export const DELAI_REGROUPEMENT = 800;

export interface Diffuseur {
  /** Ajoute un abonné ; renvoie le désabonnement. */
  abonner(rappel: () => void): () => void;
  /** Un changement est arrivé : les abonnés seront prévenus une fois la rafale passée. */
  signaler(): void;
  /** Oublie un signal en attente (à la fermeture du canal). */
  annuler(): void;
  nombreAbonnes(): number;
}

/** Prévient des abonnés, une seule fois par rafale de signaux rapprochés. */
export function creerDiffuseur(delai = DELAI_REGROUPEMENT, avant?: () => void): Diffuseur {
  const abonnes = new Set<() => void>();
  let minuterie: ReturnType<typeof setTimeout> | null = null;

  function prevenir() {
    minuterie = null;
    try {
      avant?.();
    } catch {
      /* Le préalable est un nettoyage : il ne doit pas empêcher de prévenir. */
    }
    /* Une copie : un abonné peut se désabonner pendant qu'on prévient. */
    [...abonnes].forEach((rappel) => {
      try {
        rappel();
      } catch {
        /* Un abonné qui plante n'empêche pas les autres d'être prévenus. */
      }
    });
  }

  return {
    abonner(rappel) {
      abonnes.add(rappel);
      return () => {
        abonnes.delete(rappel);
      };
    },
    signaler() {
      if (minuterie !== null) clearTimeout(minuterie);
      minuterie = setTimeout(prevenir, delai);
    },
    annuler() {
      if (minuterie !== null) clearTimeout(minuterie);
      minuterie = null;
    },
    nombreAbonnes() {
      return abonnes.size;
    },
  };
}

/** Que faire du canal, selon qu'une session existe et qu'il est déjà ouvert. */
export function decisionCanal(sessionOuverte: boolean, canalOuvert: boolean) {
  if (sessionOuverte && !canalOuvert) return 'ouvrir' as const;
  if (!sessionOuverte && canalOuvert) return 'fermer' as const;
  return 'rien' as const;
}

// ── L'abonnement unique ─────────────────────────────────────────────────────

/**
 * Ce qu'il faut faire une fois par rafale, avant de prévenir les écrans :
 * oublier les lectures en vol (src/shared/donnees/index.ts), pour qu'une
 * relecture ne rejoigne pas une requête partie avant le changement.
 */
const preambules = new Set<() => void>();

export function avantChaqueRafale(preambule: () => void): void {
  preambules.add(preambule);
}

const diffuseur = creerDiffuseur(DELAI_REGROUPEMENT, () => {
  preambules.forEach((p) => {
    p();
  });
});
let canal: RealtimeChannel | null = null;
let demarre = false;

function ouvrir(): void {
  const client = supabase();
  let premiereFois = true;
  let nouveau = client.channel('vla-en-direct');
  for (const table of TABLES_EN_DIRECT) {
    nouveau = nouveau.on('postgres_changes', { event: '*', schema: 'public', table }, () => {
      diffuseur.signaler();
    });
  }
  nouveau.subscribe((statut) => {
    if (statut !== REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) return;
    /* Après une coupure (réseau, veille du téléphone), le canal se rouvre seul,
       mais les changements survenus entre-temps sont perdus : on relit. */
    if (!premiereFois) diffuseur.signaler();
    premiereFois = false;
  });
  canal = nouveau;
}

function fermer(): void {
  diffuseur.annuler();
  if (canal) void supabase().removeChannel(canal);
  canal = null;
}

function appliquer(sessionOuverte: boolean): void {
  try {
    const decision = decisionCanal(sessionOuverte, canal !== null);
    if (decision === 'ouvrir') ouvrir();
    else if (decision === 'fermer') fermer();
  } catch {
    /* Sans direct, l'application marche comme avant : relue à l'ouverture. */
  }
}

/* Le canal suit la session : ouvert à la connexion, fermé à la déconnexion. */
function demarrer(): void {
  if (demarre) return;
  if (!environnement.ok || environnement.env.mode !== 'supabase') return;
  demarre = true;
  try {
    supabase().auth.onAuthStateChange((_evenement, session) => {
      /* Comme dans auth.supabase.ts : pas d'appel au client dans ce rappel. */
      setTimeout(() => {
        appliquer(session !== null);
      }, 0);
    });
  } catch {
    demarre = false;
  }
}

/**
 * Prévient `rappel` quand une table suivie change (une fois par rafale).
 * Le premier abonné démarre le canal. Renvoie le désabonnement.
 */
export function surChangementEnDirect(rappel: () => void): () => void {
  demarrer();
  return diffuseur.abonner(rappel);
}
